
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useParams, useLocation  } from 'react-router-dom';
import { useMsal, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import './App.css';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { ToastContainer } from "react-toastify";
import SplashCursor from './components/SplashCursor';
import "react-toastify/dist/ReactToastify.css"; // Ensure the CSS is imported
import logo from './Assets/nav-logo.png';
import loginLogo from './Assets/login_image.png';
import { loginRequest } from './authConfig';

import CvitpPage from './Pages/CvitpPage';
import FtcfPage from './Pages/FtcfPage';
import PersonalTaxDataPage from './Pages/PersonalTaxDataPage';
import CorporateTaxDataPage from './Pages/CorporateTaxDataPage';
import DueItemsPage from './Pages/DueItemsPage';
import ESignRequestPage from './Pages/ESignRequestPage';
import CustomerTaxPortalPage from './Pages/CustomerTaxPortalPage';
import FaxPage from './Pages/FaxPage';
import FaxDashboard from './components/Fax/FaxDashboard';
import PublicSendFaxPage from './Pages/PublicSendFaxPage';
import InvoicePage from './Pages/InvoicePage';
import BankStatementPage from './Pages/BankStatements/BankStatementPage';
import LetterHead from './Pages/LetterHead/LetterHead';
import ScrollToTop from './components/Common/ScrollToTop';




function SignInScreen() {
  const { instance } = useMsal();
  const [loading, setLoading] = useState(false);

  const onSignIn = () => {
    instance.loginRedirect(loginRequest).catch(e => {
      console.error(e);
    });
  };

  const handleClick = () => {
    setLoading(true);
    // The login redirect will take over, but we set loading state
    // in case there's a delay or popup blocker.
    onSignIn();
  };

  return (
    <div className="signin">
      <SplashCursor COLOR="#2f6fed" RAINBOW_MODE={false} SPLAT_FORCE={4500} DENSITY_DISSIPATION={3.5} />

      <div className="signin__panel">
        <div className="signin__content">
          <div className="signin__content-top">
            <div className="signin__logo-row">
              <img src={logo} alt="Oriental Business Solutions" className="signin__logo" />
            </div>
          </div>

          <div className="signin__content-bottom">
            <p className="signin__eyebrow">Client Platform</p>
            <h2 className="signin__title">OrientalBiz</h2>
            <p className="signin__subtitle">
              Sign in with your Microsoft account to manage client, tax tracking and reports.
            </p>
            <div className="signin__features">
              <span className="signin__feature">Client Management</span>
              <span className="signin__feature">Tax Tracking</span>
              <span className="signin__feature">Reporting</span>
              <span className="signin__feature">Invoices</span>
              <span className="signin__feature">Fax</span>
            </div>

            <button className="ms-signin-btn" onClick={handleClick} disabled={loading}>
              {loading ? (
                <span className="ms-signin-btn__spinner" />
              ) : (
                <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                </svg>
              )}
              {loading ? 'Signing in…' : 'Sign in with Microsoft'}
            </button>

            <p className="signin__footnote">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="4" y="11" width="16" height="9" rx="2" />
                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
              </svg>
              Secured by your organization&apos;s Microsoft 365 sign-in
            </p>
          </div>
        </div>

        <div className="signin__mockup">
          <img
            src={loginLogo}
            alt="Client management dashboard shown on a laptop and phone"
            className="signin__mockup-img"
          />
        </div>
      </div>
    </div>
  );
}

const DynamicRedirectExternal = () => {
    const { id } = useParams();
    const location = useLocation();
    // Reconstruct the path by replacing the param placeholder with the actual id
    const path = location.pathname.replace(id, '');

    const to = `${path}${id}`;
    return <RedirectExternal to={to} />;
};

const RedirectExternal = ({ to }) => {
    useEffect(() => {
        const url = `https://orientalbiz.ca${to}`;
        window.location.replace(url);
    }, [to]);
    return null;
};

function App() {
  const { instance, accounts } = useMsal();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // MSAL's initialization can be asynchronous. We wait for it to complete.
    // This is especially important when handling redirect flows.
    const initialize = async () => {
      await instance.initialize();
      setIsInitialized(true);
    };
    initialize();
  }, [instance]);

  // The handleRedirectPromise will resolve if the page is loaded after a redirect from AAD.
  // It will return null if there's no auth hash to process.
  useEffect(() => {
    if (isInitialized && accounts.length === 0) {
      instance.handleRedirectPromise().then((response) => {
        if (response && response.account) {
          instance.setActiveAccount(response.account);
        }
      }).catch(err => console.error(err));
    }
  }, [isInitialized, accounts.length, instance]);

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <>
      <PullToRefresh onRefresh={handleRefresh}>
        <BrowserRouter>
          <AuthenticatedTemplate>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<DueItemsPage />} />
              <Route path='/landing' element={<DueItemsPage />} />
              <Route path='/due-items' element={<DueItemsPage />} />
              <Route path='/personalTax' element={<PersonalTaxDataPage />} />
              <Route path='/personal-tax' element={<PersonalTaxDataPage />} />
              {/* Public routes available to authenticated users */}
              <Route path='/free-tax-clinic-form' element={<FtcfPage />} />
              <Route path='/cvitp' element={<CvitpPage />} />
              <Route path='/review-tax/:token' element={<CustomerTaxPortalPage />} />
              <Route path='/send-fax/:token' element={<PublicSendFaxPage />} />
              {/* Authenticated-only routes */}
              <Route path='/corporateData' element={<CorporateTaxDataPage />} />
              <Route path='/esign-request' element={<ESignRequestPage />} />
              <Route path='/send-fax' element={<FaxPage />} />
              <Route path='/fax-dashboard' element={<FaxDashboard />} />
              <Route path='/invoice' element={<InvoicePage />} />
              <Route path='/bank-statement'   element={<BankStatementPage />} />
              <Route path="/letterhead" element={<LetterHead />} />
            </Routes>
          </AuthenticatedTemplate>
          <UnauthenticatedTemplate>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<SignInScreen />} />
              <Route path='/free-tax-clinic-form' element={<FtcfPage />} />
              <Route path='/cvitp' element={<CvitpPage />} />
              <Route path='/review-tax/:token' element={<CustomerTaxPortalPage />} />
              <Route path='/send-fax/:token' element={<PublicSendFaxPage />} />

              <Route path='/home' element={<RedirectExternal to="/" />} />
              <Route path='/about' element={<RedirectExternal to="/about" />} />
              <Route path='/service' element={<RedirectExternal to="/service" />} />
              <Route path='/servicedetails/:id' element={<DynamicRedirectExternal />} />
              <Route path='/contact' element={<RedirectExternal to="/contact" />} />
              <Route path='/blog' element={<RedirectExternal to="/blog" />} />
              <Route path='/faq' element={<RedirectExternal to="/faq" />} />
              <Route path='/blogdetails/:id' element={<DynamicRedirectExternal />} />

              {/* Redirect any other unauthenticated access back to the sign-in page */}
              <Route path="*" element={<SignInScreen />} />
            </Routes>
          </UnauthenticatedTemplate>
        </BrowserRouter>
      </PullToRefresh>
      <ToastContainer />
    </>
  );
}

export default App;
